/**
 * Standardized cookie configuration for login-related cookies
 * This ensures consistent cookie settings across the entire application
 */

const LOGIN_TOKEN_COOKIE = 'hw_id_token';
const LOGIN_ACCESS_TOKEN_COOKIE = 'hw_access_token';
const LOGIN_REFRESH_TOKEN_COOKIE = 'hw_refresh_token';
const LOGIN_EXPIRES_IN_COOKIE = 'hw_expires_in';

export function useLoginCookies() {
  const runtimeConfig = useRuntimeConfig();
  const envData = runtimeConfig?.public;

  // Standard cookie configuration used throughout the application
  const getLoginCookieOptions = (maxAge = null) => {
    const baseOptions = {
      domain: envData?.COOKIE_SESSION_DOMAIN,
      path: '/',
      secure: true,
      sameSite: 'lax',
      watch: true,
    };

    if (maxAge !== null) {
      baseOptions.maxAge = maxAge;
    }

    return baseOptions;
  };

  // HttpOnly refresh token — readable/writable on server only (FBF-982 Phase 1)
  const getRefreshCookieOptions = (maxAge = null) => {
    const options = {
      ...getLoginCookieOptions(maxAge),
      httpOnly: true,
      watch: false,
    };

    return options;
  };

  // Create cookie instances with standardized configuration (JS-readable tokens only)
  const createLoginCookies = (maxAge = null) => {
    const options = getLoginCookieOptions(maxAge);

    return {
      idToken: useCookie(LOGIN_TOKEN_COOKIE, options),
      accessToken: useCookie(LOGIN_ACCESS_TOKEN_COOKIE, options),
      expiresIn: useCookie(LOGIN_EXPIRES_IN_COOKIE, options),
    };
  };

  // Get cookie values (for reading). Refresh is null on the client (HttpOnly).
  const getLoginCookieValues = () => {
    const options = getLoginCookieOptions();

    return {
      idToken: useCookie(LOGIN_TOKEN_COOKIE, options)?.value || null,
      accessToken: useCookie(LOGIN_ACCESS_TOKEN_COOKIE, options)?.value || null,
      refreshToken: useCookie(LOGIN_REFRESH_TOKEN_COOKIE, getRefreshCookieOptions())?.value || null,
      expiresIn: useCookie(LOGIN_EXPIRES_IN_COOKIE, options)?.value || null,
    };
  };

  // Clear all login cookies (refresh requires httpOnly options to match the Set-Cookie flags)
  const clearLoginCookies = () => {
    const options = getLoginCookieOptions(0);
    const refreshOptions = getRefreshCookieOptions(0);

    const idToken = useCookie(LOGIN_TOKEN_COOKIE, options);
    const accessToken = useCookie(LOGIN_ACCESS_TOKEN_COOKIE, options);
    const refreshToken = useCookie(LOGIN_REFRESH_TOKEN_COOKIE, refreshOptions);
    const expiresIn = useCookie(LOGIN_EXPIRES_IN_COOKIE, options);

    idToken.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    expiresIn.value = null;
  };

  return {
    LOGIN_TOKEN_COOKIE,
    LOGIN_ACCESS_TOKEN_COOKIE,
    LOGIN_REFRESH_TOKEN_COOKIE,
    LOGIN_EXPIRES_IN_COOKIE,
    getLoginCookieOptions,
    getRefreshCookieOptions,
    createLoginCookies,
    getLoginCookieValues,
    clearLoginCookies,
  };
}
